// Fund Menu - Browse and research Tier 1 approved funds
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { OrderModal } from '@/components/OrderModal';
import { LifecycleFundsPanel } from '@/components/investments/LifecycleFundsPanel';

export const FundMenu = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [filteredFunds, setFilteredFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAssetClass, setSelectedAssetClass] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'

  useEffect(() => {
    loadFunds();
  }, []);

  useEffect(() => {
    filterFunds();
  }, [selectedAssetClass, searchQuery, funds]);

  const loadFunds = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/investments/funds');
      setFunds(response.data.funds || []);
      setFilteredFunds(response.data.funds || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load fund list');
      console.error('Fund load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterFunds = () => {
    let filtered = funds;

    // Filter by asset class
    if (selectedAssetClass !== 'All') {
      filtered = filtered.filter(f => f.asset_class === selectedAssetClass);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f => 
        f.symbol.toLowerCase().includes(query) ||
        f.name.toLowerCase().includes(query) ||
        f.description.toLowerCase().includes(query)
      );
    }

    setFilteredFunds(filtered);
  };

  const handleBuyClick = (fund) => {
    setSelectedFund(fund);
    setOrderModalOpen(true);
  };

  const handleOrderComplete = () => {
    setOrderModalOpen(false);
    setSelectedFund(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const formatted = (value || 0).toFixed(2);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Get unique asset classes
  const assetClasses = ['All', ...new Set(funds.map(f => f.asset_class))];

  // Risk rating colors
  const getRiskColor = (rating) => {
    const colors = {
      'Very Low': 'bg-[#28A745] text-white',
      'Low': 'bg-[#34D058] text-[#24292E]',
      'Moderate': 'bg-[#0366D6] text-white',
      'Moderate-High': 'bg-[#FD7E14] text-white',
      'High': 'bg-[#D73A49] text-white'
    };
    return colors[rating] || 'bg-[#6A737D] text-white';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <p className="text-[#586069]">Loading approved funds...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030]">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1E4E8]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8B6B3B] mb-1" data-testid="fund-family-header-menu">
            UniFed Core Funds&trade; — A through E Series
          </p>
          <h1 className="text-2xl font-bold text-[#24292E]">Tier 1 Approved Funds</h1>
          <p className="text-sm text-[#586069] mt-1">
            {filteredFunds.length} funds available for investment
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* UniFed Lifecycle Series — target-date funds */}
        <div className="mb-6">
          <LifecycleFundsPanel />
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Search */}
            <div className="flex-1 w-full md:max-w-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search funds by name or symbol..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-[#D1D5DB] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
                />
                <svg className="w-5 h-5 text-[#959DA5] absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded border ${
                  viewMode === 'grid' 
                    ? 'bg-[#0366D6] text-white border-[#0366D6]' 
                    : 'bg-white text-[#586069] border-[#D1D5DB] hover:bg-[#F6F8FA]'
                }`}
                title="Grid view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded border ${
                  viewMode === 'table' 
                    ? 'bg-[#0366D6] text-white border-[#0366D6]' 
                    : 'bg-white text-[#586069] border-[#D1D5DB] hover:bg-[#F6F8FA]'
                }`}
                title="Table view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Asset Class Filter Pills */}
          <div className="flex flex-wrap gap-2 mt-4">
            {assetClasses.map((assetClass) => (
              <button
                key={assetClass}
                onClick={() => setSelectedAssetClass(assetClass)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  selectedAssetClass === assetClass
                    ? 'bg-[#0366D6] text-white'
                    : 'bg-[#F6F8FA] text-[#586069] hover:bg-[#E1E4E8]'
                }`}
              >
                {assetClass}
              </button>
            ))}
          </div>
        </div>

        {/* Funds Display */}
        {filteredFunds.length === 0 ? (
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-[#D1D5DB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-[#586069]">No funds match your search criteria</p>
          </div>
        ) : viewMode === 'grid' ? (
          // Grid View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFunds.map((fund) => (
              <div key={fund.symbol} className="bg-white rounded-lg border border-[#E1E4E8] hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* Fund Header - Clickable */}
                  <div 
                    className="flex items-start justify-between mb-3 cursor-pointer" 
                    onClick={() => navigate(`/funds/${fund.symbol}`)}
                  >
                    <div>
                      <h3 className="text-lg font-semibold text-[#0366D6] hover:text-[#0256C5] font-mono transition-colors">
                        {fund.symbol}
                      </h3>
                      <p className="text-sm text-[#586069] mt-1">{fund.name}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor(fund.risk_rating)}`}>
                      {fund.risk_rating}
                    </span>
                  </div>

                  {/* Asset Class */}
                  <div className="mb-4">
                    <span className="inline-block px-2 py-1 bg-[#F6F8FA] text-[#586069] text-xs font-medium rounded">
                      {fund.asset_class}
                    </span>
                  </div>

                  {/* Price and Performance */}
                  <div className="border-t border-[#E1E4E8] pt-4 mb-4">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-2xl font-bold text-[#24292E]">
                        {formatCurrency(fund.current_price)}
                      </span>
                      <span className={`text-sm font-medium ${
                        fund.change >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                      }`}>
                        {formatPercent(fund.change_percent)}
                      </span>
                    </div>

                    {/* Performance Metrics */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-[#586069]">YTD:</span>
                        <span className={`ml-1 font-medium ${
                          fund.ytd_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                        }`}>
                          {formatPercent(fund.ytd_return)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#586069]">1Y:</span>
                        <span className={`ml-1 font-medium ${
                          fund.one_year_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                        }`}>
                          {formatPercent(fund.one_year_return)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#586069]">3Y:</span>
                        <span className={`ml-1 font-medium ${
                          fund.three_year_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                        }`}>
                          {formatPercent(fund.three_year_return)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#586069]">5Y:</span>
                        <span className={`ml-1 font-medium ${
                          fund.five_year_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                        }`}>
                          {formatPercent(fund.five_year_return)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expense Ratio */}
                  <div className="text-xs text-[#586069] mb-4">
                    Expense Ratio: <span className="font-medium text-[#24292E]">{fund.expense_ratio}%</span>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-[#586069] mb-4 line-clamp-2">
                    {fund.description}
                  </p>

                  {/* Buy Button */}
                  <button
                    onClick={() => handleBuyClick(fund)}
                    className="w-full px-4 py-2 bg-[#0366D6] hover:bg-[#0256C5] text-white text-sm font-medium rounded transition-colors"
                  >
                    Buy
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Table View
          <div className="bg-white rounded-lg border border-[#E1E4E8] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F6F8FA] border-b border-[#E1E4E8]">
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Symbol</th>
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Asset Class</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Risk</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Price</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Change</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">YTD</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">1Y</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Expense</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFunds.map((fund) => (
                    <tr key={fund.symbol} className="border-b border-[#E1E4E8] hover:bg-[#F6F8FA] cursor-pointer">
                      <td 
                        className="py-3 px-4 font-mono font-semibold text-[#0366D6] hover:text-[#0256C5] transition-colors"
                        onClick={() => navigate(`/funds/${fund.symbol}`)}
                      >
                        {fund.symbol}
                      </td>
                      <td 
                        className="py-3 px-4 text-[#24292E]"
                        onClick={() => navigate(`/funds/${fund.symbol}`)}
                      >
                        {fund.name}
                      </td>
                      <td className="py-3 px-4 text-[#586069]">
                        {fund.asset_class}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor(fund.risk_rating)}`}>
                          {fund.risk_rating}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-[#24292E]">
                        {formatCurrency(fund.current_price)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${
                          fund.change >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                        }`}>
                          {formatPercent(fund.change_percent)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`${
                          fund.ytd_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                        }`}>
                          {formatPercent(fund.ytd_return)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`${
                          fund.one_year_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                        }`}>
                          {formatPercent(fund.one_year_return)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-[#586069]">
                        {fund.expense_ratio}%
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleBuyClick(fund)}
                          className="px-3 py-1 bg-[#0366D6] hover:bg-[#0256C5] text-white text-xs font-medium rounded transition-colors"
                        >
                          Buy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Order Modal */}
      {orderModalOpen && selectedFund && (
        <OrderModal
          fund={selectedFund}
          orderType="buy"
          onClose={handleOrderComplete}
        />
      )}
    </div>
  );
};
